import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2, Loader2, Clock, Truck, Receipt, ChevronDown, ChevronUp, Timer, Calendar, ShoppingBag, History } from "lucide-react";

import { StarRow, resolvePrimary } from "../features/avaliacao-popup";
import { contarItens, formatarQtd, medidaDoItem } from "../lib/unidadeVenda";

const API = "/api";

// Quilo/litro (30/09/2026): peso aparece sempre ("1,250 kg "); por unidade, só
// quando passa de 1 ("2× "), como antes. Quantidade quebrada nunca some.
const prefixoQtd = (it) => {
  const q = Number(it.quantidade || 1);
  const medida = medidaDoItem(it);
  if (medida) return `${formatarQtd(q, medida)} `;
  return q > 1 || !Number.isInteger(q) ? `${formatarQtd(q, null)}× ` : "";
};

const fmtBRL = (n) =>
  (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
};

const fmtDateOnly = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch (_) { return ""; }
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return ""; }
};

const fmtDuration = (startIso, endIso) => {
  if (!startIso || !endIso) return "";
  try {
    const ms = new Date(endIso) - new Date(startIso);
    if (ms <= 0) return "";
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    // < 60 min → "51min"; >= 60 min → "1h15min" (sem minutos zero: "2h")
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h${String(m).padStart(2, "0")}min`;
  } catch (_) { return ""; }
};

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  return url;
};

export default function ReviewPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState({});
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // Resumo do pedido começa EXPANDIDO. Colapsa sozinho quando o usuário rola pra baixo
  // (foco vai pro formulário) e reabre quando ele volta ao topo.
  const [showOrderDetails, setShowOrderDetails] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/public/review/${token}`);
        if (!active) return;
        setData(res.data);
        if (res.data?.review?.submitted_at) {
          setSubmitted(true);
          setRatings(res.data.review.ratings || {});
          setComment(res.data.review.comment || "");
        }
      } catch (e) {
        if (active) setError(e.response?.data?.detail || "Link de avaliação inválido ou expirado");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  // Aplicar tema dark/light no body (preserva dark padrão se não vier do tenant)
  useEffect(() => {
    const theme = (data?.tenant?.theme || "dark").toLowerCase();
    const root = document.documentElement;
    if (theme === "light") {
      root.classList.remove("dark");
    } else {
      root.classList.add("dark");
    }
  }, [data?.tenant?.theme]);

  // Auto-collapse do resumo do pedido conforme o scroll: dá espaço pro formulário
  // quando o usuário desce, e volta a exibir o resumo quando ele sobe ao topo.
  useEffect(() => {
    if (submitted) return;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 90) {
        setShowOrderDetails(false);
      } else if (y < 20) {
        setShowOrderDetails(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [submitted]);

  const { color: primary } = resolvePrimary(data?.tenant?.primary_color);

  const dimensions = data?.dimensions || [];
  const totalDims = dimensions.length;
  const answeredCount = dimensions.filter(d => Number(ratings[d.key]) > 0).length;
  const allAnswered = totalDims > 0 && answeredCount === totalDims;

  const handleSubmit = async () => {
    if (!allAnswered) {
      const missing = dimensions.find(d => !Number(ratings[d.key]) > 0);
      if (missing) alert(`Dê uma nota para "${missing.label}"`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/review/${token}`, {
        ratings,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao enviar avaliação. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgPreview = useMemo(() => {
    const vals = Object.values(ratings).filter((v) => Number(v) > 0);
    if (vals.length === 0) return 0;
    return vals.reduce((a, b) => a + Number(b), 0) / vals.length;
  }, [ratings]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primary }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-3">😕</div>
          <h1 className="text-xl font-bold mb-2">Ops…</h1>
          <p className="text-sm text-zinc-500">{error}</p>
        </div>
      </div>
    );
  }

  const { pedido, tenant } = data || {};
  // "N itens": linha por quilo/litro conta 1 (a `unidade` de cada item vem do servidor)
  const totalItems = contarItens(pedido?.items);
  // Saudação personalizada com primeiro nome do cliente (se houver). Capitaliza pra ficar amigável.
  const firstName = (pedido?.cliente_nome || "").trim().split(/\s+/)[0] || "";
  const firstNameCap = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : "";

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 pb-12">
      {/* Header com marca + cor do tenant */}
      <header
        className="text-white px-4 py-8 sm:py-10 text-center shadow-lg"
        style={{
          backgroundImage: `linear-gradient(135deg, ${primary} 0%, color-mix(in srgb, ${primary} 70%, #000) 100%)`,
        }}
      >
        <div className="max-w-xl mx-auto">
          {tenant?.logo_url ? (
            <img
              src={getImageUrl(tenant.logo_url)}
              alt={tenant?.name || ""}
              className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-2xl bg-white/15 backdrop-blur p-2 object-contain shadow-lg mb-3"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg mb-3">
              <span className="text-3xl">🍽️</span>
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{tenant?.name || "Núcleo"}</h1>
          {!submitted ? (
            <p className="text-base sm:text-lg text-white/95 mt-1 font-medium">
              {firstNameCap ? `${firstNameCap}, avalie seu pedido:` : "Avalie seu pedido:"}
            </p>
          ) : (
            <p className="text-base sm:text-lg text-white/95 mt-1 font-medium">
              {firstNameCap ? `Obrigado, ${firstNameCap}! 💛` : "Obrigado pela avaliação!"}
            </p>
          )}
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 -mt-6 sm:-mt-8">
        {/* Tela de agradecimento */}
        {submitted ? (
          <>
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 p-6 sm:p-8 text-center">
              <div
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: `color-mix(in srgb, ${primary} 18%, transparent)` }}
              >
                <CheckCircle2 className="w-9 h-9" strokeWidth={2.2} style={{ color: primary }} />
              </div>
              <h2 className="text-xl font-bold mb-1">Avaliação enviada!</h2>
              <p className="text-sm text-zinc-500">Sua opinião é muito importante 💛</p>

              {/* Notas detalhadas — uma linha por dimensão respondida */}
              {dimensions.length > 0 && (
                <div className="mt-6 space-y-2 text-left">
                  {dimensions.map((d) => {
                    const v = Number(ratings[d.key]) || 0;
                    if (!v) return null;
                    return (
                      <div key={d.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-zinc-50 dark:bg-zinc-800/50">
                        <span className="text-sm font-medium truncate">{d.label}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              className="w-4 h-4"
                              style={n <= v
                                ? { fill: primary, color: primary }
                                : { color: "rgb(228 228 231 / 0.6)" }}
                              strokeWidth={1.5}
                            />
                          ))}
                          <span className="text-xs font-bold ml-1" style={{ color: primary }}>{v}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Média geral em destaque */}
              {avgPreview > 0 && (
                <div
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${primary} 18%, transparent)`,
                    color: primary,
                  }}
                >
                  <Star className="w-4 h-4" style={{ fill: primary, color: primary }} />
                  <span className="font-bold">Média {avgPreview.toFixed(1).replace(".", ",")}</span>
                  <span className="text-xs opacity-80">/ 5,0</span>
                </div>
              )}

              {/* CTA: voltar a pedir */}
              {tenant?.slug && (
                <a
                  href={`https://${tenant.slug}.usenucleo.com.br/#/cardapio`}
                  className="mt-6 w-full h-12 rounded-xl text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${primary}, color-mix(in srgb, ${primary} 70%, #000))`,
                    boxShadow: `0 10px 30px -10px ${primary}`,
                  }}
                >
                  <ShoppingBag className="w-4 h-4" />
                  Vamos pedir de novo
                </a>
              )}
            </div>

            {/* Histórico de avaliações anteriores do mesmo cliente */}
            {(data?.history || []).length > 0 && (
              <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden mt-4">
                <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-2">
                  <History className="w-4 h-4 text-zinc-500" />
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">
                    Suas avaliações anteriores
                  </h3>
                </div>
                <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {data.history.map((h, i) => (
                    <li key={i} className="px-5 py-3 flex items-start gap-3">
                      <div
                        className="flex flex-col items-center justify-center w-12 h-12 rounded-xl shrink-0"
                        style={{ backgroundColor: `color-mix(in srgb, ${primary} 14%, transparent)` }}
                      >
                        <Star className="w-3 h-3" style={{ fill: primary, color: primary }} />
                        <span className="text-sm font-extrabold" style={{ color: primary }}>
                          {Number(h.rating_avg || 0).toFixed(1).replace(".", ",")}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold">
                            Pedido #{String(h.pedido_codigo || "").replace(/^#+/, "")}
                          </span>
                          <span className="text-[11px] text-zinc-400">{fmtDateOnly(h.submitted_at)}</span>
                        </div>
                        {h.comment && (
                          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2 italic">"{h.comment}"</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Resumo do pedido — sticky no topo, colapsa/expande conforme scroll */}
            {pedido && (
              <div className="sticky top-2 sm:top-3 z-20 mb-5">
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowOrderDetails(v => !v)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Receipt className="w-4 h-4 text-zinc-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                          Pedido #{String(pedido.codigo || "").replace(/^#+/, "")}
                        </p>
                        <p className="text-sm font-semibold truncate">
                          {totalItems} {totalItems === 1 ? "item" : "itens"} · <span style={{ color: primary }}>{fmtBRL(pedido.total)}</span>
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-zinc-500 flex items-center gap-1 shrink-0 ml-2">
                      {showOrderDetails ? "Ocultar" : "Ver"}
                      {showOrderDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </button>
                  {/* Grid trick pra animar altura sem precisar saber o tamanho */}
                  <div
                    className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                    style={{ gridTemplateRows: showOrderDetails ? "1fr" : "0fr" }}
                  >
                    <div className="overflow-hidden">
                      <div className="border-t border-zinc-100 dark:border-zinc-800 p-4 space-y-3">
                        {(pedido.items || []).map((it, i) => (
                          <div key={i} className="flex gap-3">
                            {it.photo_url ? (
                              <img
                                src={getImageUrl(it.photo_url)}
                                alt={it.nome}
                                className="w-12 h-12 rounded-lg object-cover bg-zinc-100 dark:bg-zinc-800 flex-shrink-0"
                                onError={(e) => { e.target.style.visibility = "hidden"; }}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center flex-shrink-0 text-lg">🍽️</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">
                                {prefixoQtd(it)}{it.nome}
                              </p>
                              {(it.subitems || []).length > 0 && (
                                <p className="text-[11px] text-zinc-500 truncate">
                                  {it.subitems.map((s) => s.nome).filter(Boolean).join(", ")}
                                </p>
                              )}
                            </div>
                            <span className="text-sm font-bold text-zinc-700 dark:text-zinc-300 flex-shrink-0">
                              {fmtBRL((it.preco || 0) * (it.quantidade || 1))}
                            </span>
                          </div>
                        ))}
                        <div className="border-t border-zinc-100 dark:border-zinc-800 pt-2 mt-2 space-y-1">
                          {(pedido.valor_entrega || 0) > 0 && (
                            <div className="flex justify-between text-xs text-zinc-500">
                              <span>Taxa de entrega</span>
                              <span>{fmtBRL(pedido.valor_entrega)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm font-bold">
                            <span>Total</span>
                            <span style={{ color: primary }}>{fmtBRL(pedido.total)}</span>
                          </div>
                          {pedido.entregador_nome && (
                            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 pt-1 border-t border-zinc-200/60 dark:border-zinc-800/60 mt-1">
                              <Truck className="w-3.5 h-3.5" />
                              <span>Entregue por <strong className="text-zinc-700 dark:text-zinc-300">{pedido.entregador_nome}</strong></span>
                            </div>
                          )}
                        </div>
                        {/* Linha do tempo do pedido: criação · conclusão · duração */}
                        {(pedido.created_at || pedido.ts_concluido) && (
                          <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-0.5">
                                <Calendar className="w-3 h-3" /> Criado
                              </div>
                              <p className="text-base font-extrabold leading-tight">
                                {fmtTime(pedido.created_at)}
                              </p>
                              <p className="text-[10px] text-zinc-500">{fmtDateOnly(pedido.created_at)}</p>
                            </div>
                            <div className="text-center border-l border-r border-zinc-100 dark:border-zinc-800">
                              <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-0.5">
                                <CheckCircle2 className="w-3 h-3" /> Concluído
                              </div>
                              <p className="text-base font-extrabold leading-tight">
                                {fmtTime(pedido.ts_concluido)}
                              </p>
                              <p className="text-[10px] text-zinc-500">{fmtDateOnly(pedido.ts_concluido)}</p>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-0.5">
                                <Timer className="w-3 h-3" /> Tempo total
                              </div>
                              <p className="text-base font-extrabold leading-tight" style={{ color: primary }}>
                                {fmtDuration(pedido.created_at, pedido.ts_concluido) || "—"}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Indicador de progresso */}
            {totalDims > 1 && (
              <div className="mb-4 flex items-center justify-between px-1">
                <div className="flex items-center gap-1.5">
                  {dimensions.map((d, i) => {
                    const done = Number(ratings[d.key]) > 0;
                    return (
                      <span
                        key={d.key}
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: done ? "32px" : "16px",
                          backgroundColor: done ? primary : "rgb(228 228 231 / 1)",
                        }}
                      />
                    );
                  })}
                </div>
                <span className="text-xs text-zinc-500 font-medium">
                  {answeredCount} de {totalDims}
                </span>
              </div>
            )}

            {/* Formulário de avaliação */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 p-5 sm:p-6">
              <div className="space-y-7 sm:space-y-8">
                {dimensions.map((d, idx) => {
                  const done = Number(ratings[d.key]) > 0;
                  return (
                    <div key={d.key} className="relative">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <span
                          className={`text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                            done ? "text-white" : "bg-zinc-200 dark:bg-zinc-800 text-zinc-500"
                          }`}
                          style={done ? { backgroundColor: primary } : undefined}
                        >
                          {done ? "✓" : idx + 1}
                        </span>
                        <p className="text-base font-bold text-center">{d.label}</p>
                      </div>
                      <StarRow
                        value={ratings[d.key] || 0}
                        onChange={(v) => setRatings({ ...ratings, [d.key]: v })}
                        primary={primary}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="mt-7 sm:mt-8 pt-6 border-t border-zinc-100 dark:border-zinc-800">
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                  Quer contar algo a mais? <span className="text-zinc-400 normal-case font-normal">(opcional)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Conta pra gente o que você achou…"
                  className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950/60 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ "--tw-ring-color": primary }}
                />
                <p className="text-[10px] text-zinc-400 text-right mt-1">{comment.length}/500</p>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !allAnswered}
                className="mt-5 w-full h-14 rounded-xl text-white font-bold text-base shadow-lg transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  backgroundImage: allAnswered
                    ? `linear-gradient(135deg, ${primary}, color-mix(in srgb, ${primary} 70%, #000))`
                    : undefined,
                  backgroundColor: !allAnswered ? "#71717a" : undefined,
                  boxShadow: allAnswered ? `0 10px 30px -10px ${primary}` : undefined,
                }}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" /> Enviando…
                  </span>
                ) : !allAnswered ? (
                  `Faltam ${totalDims - answeredCount} nota${totalDims - answeredCount > 1 ? "s" : ""}`
                ) : (
                  "Enviar avaliação"
                )}
              </button>
            </div>
          </>
        )}

        <p className="text-center text-[11px] text-zinc-400 mt-6">
          Avaliação feita via <strong className="text-zinc-500">Núcleo</strong>
        </p>
      </div>
    </div>
  );
}
